// ============================================================================
// szojegyzekView.js — a KÖZÖS SZÓJEGYZÉK panelje (2026-10-08, tagi kérésre).
//
// A kutatók maguk bővítik: kifejezések, nevek, helynevek, a 15 csevegési nyelven. Minden mentés új
// változatot (v1, v2, …) hoz létre; a "Változások" lista mutatja, ki mit és mikor módosított.
// Az adatot és az ellenőrzést a szerver adja (functions/src/glossary.js); ez a nézet csak megjelenít.
// A nézet TISZTA: minden állapot a modellben, minden művelet a kezelőkben (main.js).
// ============================================================================

import { t, localeFor } from "./i18n.js";
import { el } from "./dom.js";


const TIPUS_KULCS = { term: "glossary.kindTerm", name: "glossary.kindName", place: "glossary.kindPlace" };
const MUVELET_KULCS = { add: "glossary.actionAdd", update: "glossary.actionUpdate", delete: "glossary.actionDelete" };

/** Egy bejegyzés illeszkedik-e a keresőszóra (alak, megjelenített alak, leírás, bármely nyelv)? */
export function szojegyzekSzuro(entry, kereses) {
  const q = String(kereses || "").trim().toLowerCase();
  if (!q) return true;
  const mezok = [entry.label, entry.description, entry.note, ...(entry.forms || []), ...Object.values(entry.translations || {})];
  return mezok.some((m) => typeof m === "string" && m.toLowerCase().includes(q));
}

/** Üres piszkozat egy új bejegyzéshez. */
export function uresPiszkozat() {
  return { id: null, kind: "term", formsText: "", label: "", description: "", note: "", translations: {} };
}

/** Bejegyzésből szerkeszthető piszkozat (az alakok vesszővel elválasztott szövegként). */
export function piszkozatBejegyzesbol(entry) {
  return {
    id: entry.id,
    kind: entry.kind || "term",
    formsText: (entry.forms || []).join(", "),
    label: entry.label || "",
    description: entry.description || "",
    note: entry.note || "",
    translations: { ...(entry.translations || {}) },
  };
}

/** Piszkozatból a szervernek küldendő bejegyzés. */
export function bejegyzesPiszkozatbol(p) {
  return {
    ...(p.id ? { id: p.id } : {}),
    kind: p.kind,
    forms: String(p.formsText || "").split(",").map((s) => s.trim()).filter(Boolean),
    label: p.label,
    description: p.description,
    note: p.note,
    translations: Object.fromEntries(Object.entries(p.translations || {}).filter(([, v]) => String(v || "").trim())),
  };
}

function idopont(ms, uiLang) {
  if (typeof ms !== "number") return "";
  try {
    return new Date(ms).toLocaleString(localeFor(uiLang), { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" });
  } catch (e) {
    return new Date(ms).toISOString();
  }
}

/** A fordítások listája úgy, hogy a felhasználó SAJÁT nyelve az első (v161, titkár #99); a többi az eredeti sorrendben marad. */
export function sajatNyelvElol(translations, sajatNyelv) {
  const lista = Object.entries(translations || {});
  const sajat = lista.filter(([k]) => k === sajatNyelv);
  return sajat.concat(lista.filter(([k]) => k !== sajatNyelv));
}

/**
 * @param {HTMLElement} app
 * @param {object} model { uiLang, betolt, hibaKulcs, hibaSzoveg, statusz, data: {version, builtIn, terms, languages,
 *   canDelete, updatedByName, updatedAt}, kereses, piszkozat, busy, tortenet (null | [..]), nyelvNevek }
 * @param {object} handlers
 */
export function renderGlossaryPanel(app, model, handlers) {
  const { uiLang, data, piszkozat, busy } = model;
  const backdrop = el("div", { class: "sheet-backdrop open" });
  const sheet = el("div", { class: "sheet tall glossary-sheet" });
  sheet.appendChild(el("div", { class: "sheet-handle" }));
  sheet.appendChild(el("h2", { text: t(uiLang, "glossary.open") }));
  backdrop.addEventListener("click", (e) => { if (e.target === backdrop) handlers.onClose(); });
  backdrop.appendChild(sheet);
  app.appendChild(backdrop);

  if (model.hibaKulcs || model.hibaSzoveg) {
    sheet.appendChild(el("div", { class: "panel-status err", text: model.hibaSzoveg || t(uiLang, model.hibaKulcs) }));
  }
  if (model.statusz) sheet.appendChild(el("div", { class: "panel-status ok", text: model.statusz }));

  if (!data) {
    sheet.appendChild(el("p", { class: "hint", text: model.betolt ? t(uiLang, "common.loading") : "" }));
    sheet.appendChild(el("button", { type: "button", class: "neutral-btn", text: t(uiLang, "glossary.cancel"), onclick: handlers.onClose }));
    return;
  }

  // --- Fejléc: változat, utolsó módosítás, súgó
  sheet.appendChild(el("p", { class: "glossary-version", text: t(uiLang, "glossary.version", { n: data.version }) }));
  if (data.builtIn) sheet.appendChild(el("p", { class: "hint", text: t(uiLang, "glossary.builtIn") }));
  else if (data.updatedByName || data.updatedAt) {
    sheet.appendChild(el("p", { class: "hint", text: t(uiLang, "glossary.updatedBy", { name: data.updatedByName || "?", time: idopont(data.updatedAt, uiLang) }) }));
  }
  sheet.appendChild(el("p", { class: "hint", text: t(uiLang, "glossary.hint") }));

  // --- Szerkesztő (új vagy meglévő bejegyzés)
  if (piszkozat) {
    sheet.appendChild(renderSzerkeszto(uiLang, model, handlers));
    return;
  }

  // --- Kereső + új bejegyzés
  const sor = el("div", { class: "panel-row glossary-toolbar" });
  const kereso = el("input", { type: "search", class: "panel-input", placeholder: t(uiLang, "glossary.search") });
  kereso.value = model.kereses || "";
  sor.appendChild(kereso);
  sor.appendChild(el("button", { type: "button", class: "primary-btn compact", text: t(uiLang, "glossary.new"), disabled: busy, onclick: handlers.onNew }));
  sheet.appendChild(sor);

  // --- Lista
  // A keresés HELYBEN szűr (sorok elrejtése), újrarajzolás nélkül: így a beviteli mező fókusza és a
  // kurzor a helyén marad gépelés közben.
  const lista = el("div", { class: "panel-list glossary-list" });
  const ures = el("p", { class: "hint glossary-empty", text: t(uiLang, "glossary.empty") });
  const sorok = [];
  for (const e of data.terms || []) {
    const s = el("div", { class: "user-row glossary-row" });
    sorok.push([s, e]);
    const szoveg = el("div", { class: "user-row-text" });
    szoveg.appendChild(el("div", { class: "primary", text: `${e.label}  ·  ${t(uiLang, TIPUS_KULCS[e.kind] || "glossary.kindTerm")}` }));
    const nyelvek = sajatNyelvElol(e.translations, model.contentLang || uiLang).map(([k, v]) => `${k.toUpperCase()}: ${v}`).join(" | ");
    const al = [e.description, nyelvek].filter(Boolean).join(" — ");
    if (al) szoveg.appendChild(el("div", { class: "sub", text: al }));
    s.appendChild(szoveg);
    const gombok = el("div", { class: "row-actions" });
    gombok.appendChild(el("button", { type: "button", class: "neutral-btn", text: t(uiLang, "glossary.edit"), disabled: busy, onclick: () => handlers.onEdit(e) }));
    if (data.canDelete) {
      gombok.appendChild(el("button", { type: "button", class: "danger-btn", text: t(uiLang, "glossary.delete"), disabled: busy, onclick: () => handlers.onDelete(e) }));
    }
    s.appendChild(gombok);
    lista.appendChild(s);
  }
  lista.appendChild(ures);
  const szur = () => {
    let lathato = 0;
    for (const [s, e] of sorok) {
      const ok = szojegyzekSzuro(e, kereso.value);
      s.style.display = ok ? "" : "none";
      if (ok) lathato++;
    }
    ures.style.display = lathato === 0 ? "" : "none";
  };
  kereso.addEventListener("input", () => {
    handlers.onSearch(kereso.value);
    szur();
  });
  szur();
  sheet.appendChild(lista);

  // --- Változások
  const tortenetGomb = el("button", { type: "button", class: "text-btn glossary-history-btn", text: t(uiLang, "glossary.history"), onclick: handlers.onToggleHistory });
  sheet.appendChild(tortenetGomb);
  if (Array.isArray(model.tortenet)) {
    const box = el("div", { class: "glossary-history" });
    for (const r of model.tortenet) {
      box.appendChild(el("div", {
        class: "sub",
        text: t(uiLang, "glossary.historyLine", {
          v: r.version, action: t(uiLang, MUVELET_KULCS[r.action] || "glossary.actionUpdate"),
          label: r.label || r.termId || "", who: r.byName || "?", time: idopont(r.at, uiLang),
        }),
      }));
    }
    if (model.tortenet.length === 0) box.appendChild(el("p", { class: "hint", text: t(uiLang, "glossary.empty") }));
    sheet.appendChild(box);
  }

  sheet.appendChild(el("button", { type: "button", class: "neutral-btn glossary-close", text: t(uiLang, "glossary.close"), onclick: handlers.onClose }));
}

function renderSzerkeszto(uiLang, model, handlers) {
  const { piszkozat: p, data, busy } = model;
  const box = el("section", { class: "panel-section glossary-editor" });
  const mezo = (labelKey, ertek, onInput, opts = {}) => {
    box.appendChild(el("label", { class: "panel-label small", text: opts.labelText || t(uiLang, labelKey) }));
    const input = el(opts.textarea ? "textarea" : "input", { class: "panel-input", maxlength: String(opts.max || 200), ...(opts.textarea ? { rows: "2" } : { type: "text" }) });
    input.value = ertek || "";
    input.addEventListener("input", () => onInput(input.value));
    box.appendChild(input);
  };

  box.appendChild(el("label", { class: "panel-label small", text: t(uiLang, "glossary.kind") }));
  const tipus = el("select", { class: "panel-input" });
  for (const k of ["term", "name", "place"]) {
    const o = el("option", { value: k, text: t(uiLang, TIPUS_KULCS[k]) });
    if (p.kind === k) o.selected = true;
    tipus.appendChild(o);
  }
  tipus.addEventListener("change", () => handlers.onDraftChange("kind", tipus.value));
  box.appendChild(tipus);

  mezo("glossary.forms", p.formsText, (v) => handlers.onDraftChange("formsText", v), { max: 1000 });
  mezo("glossary.label", p.label, (v) => handlers.onDraftChange("label", v), { max: 120 });
  mezo("glossary.description", p.description, (v) => handlers.onDraftChange("description", v), { max: 300, textarea: true });

  box.appendChild(el("h4", { class: "glossary-sub", text: t(uiLang, "glossary.translations") }));
  for (const kod of data.languages || []) {
    const nev = (model.nyelvNevek && model.nyelvNevek[kod]) || kod;
    mezo(null, (p.translations || {})[kod], (v) => handlers.onDraftTranslation(kod, v), { labelText: `${nev} (${kod})`, max: 200 });
  }
  mezo("glossary.note", p.note, (v) => handlers.onDraftChange("note", v), { max: 500, textarea: true });

  const gombok = el("div", { class: "panel-row" });
  gombok.appendChild(el("button", { type: "button", class: "neutral-btn", text: t(uiLang, "glossary.cancel"), disabled: busy, onclick: handlers.onCancelDraft }));
  gombok.appendChild(el("button", { type: "button", class: "primary-btn compact", text: busy ? "…" : t(uiLang, "glossary.save"), disabled: busy, onclick: handlers.onSave }));
  box.appendChild(gombok);
  return box;
}
